import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImpersonationAuditService, ImpersonationAuditEntry } from 'src/app/services/impersonation-audit.service';
import { ImpersonationService, ImpersonationSession } from 'src/app/services/impersonation.service';
import { ActiveUserService } from 'src/app/services/active-user.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { DataGridColumn } from 'src/app/utils/interfaces';
import { Utils } from 'src/app/utils/utils';
import { UserInfo } from '@memberjunction/core';
import { ImpersonationAuditLogEntity } from 'mj_generatedentities';
import { EntityNames } from '../../utils/constants';
import { UserEntity } from '@memberjunction/core-entities';

/**
 * Consolidated session log for display (one row per session)
 */
interface ConsolidatedSessionLog {
  sessionId: string;
  originalUserId: string;
  originalUserName: string;
  impersonatedUserId: string;
  impersonatedUserName: string;
  startTime: Date | null;
  endTime: Date | null;
  duration: string;
  status: string;
  reason: string;
  clientIP: string | null;
  actionCount: number;
}

/**
 * Component for viewing impersonation audit logs and managing active sessions
 * Provides comprehensive audit trail and admin controls for security compliance
 */
@Component({
  selector: 'app-impersonation-logs',
  templateUrl: './impersonation-logs.html',
  styleUrls: ['./impersonation-logs.scss']
})
export class ImpersonationLogsComponent implements OnInit, OnDestroy {
  // Historical audit logs (consolidated by session)
  auditLogs: ConsolidatedSessionLog[] = [];
  loadingLogs: boolean = false;

  // Active sessions
  activeSessions: ImpersonationSession[] = [];
  loadingActiveSessions: boolean = false;

  // Current user
  currentUser: UserInfo | null = null;

  // Grid columns for audit logs (consolidated by session)
  auditLogColumns: DataGridColumn[] = [
    { key: 'sessionId', label: 'Session ID', type: 'text', sortable: true, filterable: true, width: '120px', align: 'left' },
    { key: 'originalUserName', label: 'Staff User', type: 'text', sortable: true, filterable: true, dropdownFilter: true, width: '180px', align: 'left' },
    { key: 'impersonatedUserName', label: 'Impersonated User', type: 'text', sortable: true, filterable: true, dropdownFilter: true, width: '180px', align: 'left' },
    {
      key: 'startTime',
      label: 'Session Start',
      type: 'date',
      sortable: true,
      filterable: false,
      width: '180px',
      align: 'left',
      format: (value: Date | null) => value ? this.formatTimestamp(value) : 'N/A'
    },
    {
      key: 'endTime',
      label: 'Session End',
      type: 'date',
      sortable: true,
      filterable: false,
      width: '180px',
      align: 'left',
      format: (value: Date | null) => value ? this.formatTimestamp(value) : 'Active'
    },
    { key: 'duration', label: 'Duration', type: 'text', sortable: true, filterable: false, width: '120px', align: 'center' },
    {
      key: 'status',
      label: 'Status',
      type: 'badge',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '120px',
      align: 'center',
      filterOptions: [
        { raw: 'Ended', formatted: 'Ended' },
        { raw: 'Expired', formatted: 'Expired' },
        { raw: 'Active', formatted: 'Active' },
        { raw: 'Forced End', formatted: 'Forced End' }
      ],
      format: (value: string) => this.getStatusBadge(value)
    },
    { key: 'reason', label: 'Reason', type: 'text', sortable: true, filterable: false, width: '200px', align: 'left' },
    { key: 'clientIP', label: 'Client IP', type: 'text', sortable: true, filterable: true, width: '130px', align: 'left' }
  ];

  // Grid columns for active sessions
  activeSessionColumns: DataGridColumn[] = [
    {
      key: 'startedAt',
      label: 'Started',
      type: 'date',
      sortable: true,
      filterable: false,
      width: '150px',
      format: (value: Date) => this.utils.formatRelativeDate(value.toISOString()),
      align: 'left'
    },
    {
      key: 'expiresAt',
      label: 'Expires',
      type: 'date',
      sortable: true,
      filterable: false,
      width: '150px',
      format: (value: Date) => this.utils.formatRelativeDate(value.toISOString()),
      align: 'left'
    },
    {
      key: 'originalUser',
      label: 'Staff User',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '180px',
      align: 'left',
      format: (value: UserInfo) => value.Name
    },
    {
      key: 'impersonatedUser',
      label: 'Impersonated User',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '180px',
      align: 'left',
      format: (value: UserInfo) => value.Name
    },
    { key: 'reason', label: 'Reason', type: 'text', sortable: true, filterable: false, width: '200px', align: 'left' },
    {
      key: 'timeRemaining',
      label: 'Time Remaining',
      type: 'custom',
      sortable: true,
      filterable: false,
      width: '140px',
      align: 'center',
      format: (value: any, item: ImpersonationSession) => this.formatTimeRemaining(item)
    },
    {
      key: 'actions',
      label: 'Actions',
      type: 'actions',
      sortable: false,
      filterable: false,
      width: '100px',
      align: 'center',
      actions: [
        {
          icon: '🛑',
          label: 'End Session',
          handler: (item: ImpersonationSession) => this.endSession(item)
        }
      ]
    }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private auditService: ImpersonationAuditService,
    private impersonationService: ImpersonationService,
    private activeUserService: ActiveUserService,
    private notificationService: NotificationService,
    private dataFetchService: DataFetchService,
    public utils: Utils
  ) {}

  async ngOnInit(): Promise<void> {
    // Get current user
    this.activeUserService.activeUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userInfo => {
        if (userInfo) {
          this.currentUser = userInfo.activeUser;
        }
      });

    // Load initial data
    await this.loadAuditLogs();
    await this.loadActiveSessions();

    // Refresh active sessions every 30 seconds
    setInterval(() => this.loadActiveSessions(), 30000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads all historical audit logs for all users and consolidates by session
   */
  async loadAuditLogs(): Promise<void> {
    this.loadingLogs = true;
    try {
      // Use parallel queries to get audit logs and users
      const [auditLogsResult, usersResult] = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.ImpersonationAuditLogs,
          ExtraFilter: '', // No filter - get all logs
          OrderBy: 'Timestamp DESC',
          ResultType: 'simple'
        },
        {
          EntityName: EntityNames.Users,
          ResultType: 'simple'
        }
      ]);

      if (!auditLogsResult.success || !usersResult.success) {
        console.error('Error fetching audit logs or users');
        this.notificationService.error(
          'Error Loading Logs',
          'Failed to load impersonation audit logs. Please try again.',
          5000
        );
        return;
      }

      const auditLogs = auditLogsResult.data as ImpersonationAuditLogEntity[];
      const users = usersResult.data as UserEntity[];

      // Create user lookup map for performance
      const userMap = new Map();
      users.forEach(user => userMap.set(user.ID, user.FirstLast));

      // Consolidate logs by session ID
      this.auditLogs = this.consolidateLogsBySession(auditLogs, userMap);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      this.notificationService.error(
        'Error Loading Logs',
        'Failed to load impersonation audit logs. Please try again.',
        5000
      );
    } finally {
      this.loadingLogs = false;
    }
  }

  /**
   * Consolidates audit logs by session ID to show one row per session
   */
  private consolidateLogsBySession(
    logs: ImpersonationAuditLogEntity[],
    userMap: Map<string, string>
  ): ConsolidatedSessionLog[] {
    // Group logs by session ID
    const sessionMap = new Map<string, ImpersonationAuditLogEntity[]>();

    logs.forEach(log => {
      const sessionId = log.ImpersonationSessionID;
      if (!sessionMap.has(sessionId)) {
        sessionMap.set(sessionId, []);
      }
      sessionMap.get(sessionId)!.push(log);
    });

    // Create consolidated session records
    const consolidated: ConsolidatedSessionLog[] = [];

    sessionMap.forEach((sessionLogs, sessionId) => {
      // Find session start and end logs
      const startLog = sessionLogs.find(l => l.AuditAction === 'SessionStart');
      const endLog = sessionLogs.find(l =>
        l.AuditAction === 'SessionEnd' ||
        l.AuditAction === 'SessionExpire' ||
        l.AuditAction === 'ForceEnd'
      );

      // Get first log for basic info (all logs in session have same user info)
      const firstLog = sessionLogs[0];

      // Calculate duration
      let duration = 'N/A';
      let status = 'Active';

      if (startLog && endLog) {
        const startTime = new Date(startLog.Timestamp);
        const endTime = new Date(endLog.Timestamp);
        const durationMs = endTime.getTime() - startTime.getTime();
        duration = this.formatDuration(durationMs);

        // Determine status based on end action
        if (endLog.AuditAction === 'SessionEnd') {
          status = 'Ended';
        } else if (endLog.AuditAction === 'SessionExpire') {
          status = 'Expired';
        } else if (endLog.AuditAction === 'ForceEnd') {
          status = 'Forced End';
        }
      } else if (startLog && !endLog) {
        // Session started but not ended - calculate current duration
        const startTime = new Date(startLog.Timestamp);
        const now = new Date();
        const durationMs = now.getTime() - startTime.getTime();
        duration = this.formatDuration(durationMs) + ' (ongoing)';
        status = 'Active';
      }

      // Parse reason from start log
      let reason = 'N/A';
      if (startLog) {
        const details = this.parseActionDetails(startLog.ActionDetails);
        reason = details.reason || 'N/A';
      }

      // Count total actions (excluding start/end)
      const actionCount = sessionLogs.filter(l =>
        !['SessionStart', 'SessionEnd', 'SessionExpire', 'ForceEnd'].includes(l.AuditAction)
      ).length;

      consolidated.push({
        sessionId,
        originalUserId: firstLog.OriginalUser_ID,
        originalUserName: userMap.get(firstLog.OriginalUser_ID) || 'Unknown User',
        impersonatedUserId: firstLog.ImpersonatedUser_ID,
        impersonatedUserName: userMap.get(firstLog.ImpersonatedUser_ID) || 'Unknown User',
        startTime: startLog ? new Date(startLog.Timestamp) : null,
        endTime: endLog ? new Date(endLog.Timestamp) : null,
        duration,
        status,
        reason,
        clientIP: firstLog.ClientIP,
        actionCount
      });
    });

    // Sort by start time (most recent first)
    return consolidated.sort((a, b) => {
      if (!a.startTime) return 1;
      if (!b.startTime) return -1;
      return b.startTime.getTime() - a.startTime.getTime();
    });
  }

  /**
   * Formats duration in milliseconds to human-readable string
   */
  private formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Parses action details JSON string safely
   */
  private parseActionDetails(actionDetailsJson: string | null): any {
    if (!actionDetailsJson) {
      return {};
    }

    try {
      return JSON.parse(actionDetailsJson);
    } catch (error) {
      console.error('Error parsing action details:', error);
      return { error: 'Invalid JSON', raw: actionDetailsJson };
    }
  }

  /**
   * Loads all currently active impersonation sessions
   */
  async loadActiveSessions(): Promise<void> {
    this.loadingActiveSessions = true;
    try {
      const sessions = await this.impersonationService.getActiveSessions();
      this.activeSessions = sessions;
    } catch (error) {
      console.error('Error loading active sessions:', error);
      this.notificationService.error(
        'Error Loading Sessions',
        'Failed to load active impersonation sessions. Please try again.',
        5000
      );
    } finally {
      this.loadingActiveSessions = false;
    }
  }

  /**
   * Ends an active impersonation session
   */
  async endSession(session: ImpersonationSession): Promise<void> {
    if (!this.currentUser) {
      this.notificationService.error('Error', 'Current user not available', 3000);
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to end the impersonation session for ${session.impersonatedUser.Name}?\n\n` +
      `Staff User: ${session.originalUser.Name}\n` +
      `Started: ${this.formatTimestamp(session.startedAt)}`
    );

    if (!confirmed) {
      return;
    }

    try {
      const result = await this.impersonationService.forceEndSession(
        session.id,
        this.currentUser
      );

      if (result.success) {
        this.notificationService.success(
          'Session Ended',
          `Successfully ended impersonation session for ${session.impersonatedUser.Name}`,
          5000
        );

        // Reload active sessions to reflect the change
        await this.loadActiveSessions();

        // Optionally reload audit logs to show the forced end action
        await this.loadAuditLogs();
      } else {
        this.notificationService.error(
          'Failed to End Session',
          result.message,
          5000
        );
      }
    } catch (error) {
      console.error('Error ending session:', error);
      this.notificationService.error(
        'Error',
        'An unexpected error occurred while ending the session.',
        5000
      );
    }
  }

  /**
   * Gets status badge HTML for display
   */
  getStatusBadge(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Ended': '✅ Ended',
      'Expired': '⏳ Expired',
      'Active': '🟢 Active',
      'Forced End': '🛑 Forced End'
    };
    return statusMap[status] || status;
  }

  /**
   * Formats time remaining for active session
   */
  formatTimeRemaining(session: ImpersonationSession): string {
    const now = new Date().getTime();
    const expiresAt = new Date(session.expiresAt).getTime();
    const remainingMs = expiresAt - now;

    if (remainingMs <= 0) {
      return 'Expired';
    }

    const minutes = Math.floor(remainingMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Formats timestamp for display (date + time)
   */
  formatTimestamp(date: Date): string {
    if (!date) return '';

    try {
      const d = new Date(date);
      const dateStr = this.utils.formatDate(d.toISOString());
      const timeStr = d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} ${timeStr}`;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  }
}
